import * as Yup from "yup";
import _isEqual from "lodash/isEqual";
import { describeAppPagesChanges } from "./CustomViews/utils";

// Collapse nested strings that are themselves JSON-encoded (backend re-encodes
// leaves, e.g. a phone comes back as "\"+1284\""); leaves plain/number strings.
const deepUnwrapJsonStrings = (v) => {
  if (typeof v === "string") {
    const t = v.trim();
    if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
      try {
        const inner = JSON.parse(t);
        if (typeof inner === "string") return deepUnwrapJsonStrings(inner);
      } catch {
        /* not valid JSON — leave as-is */
      }
    }
    return v;
  }
  if (Array.isArray(v)) return v.map(deepUnwrapJsonStrings);
  if (v && typeof v === "object") {
    return Object.fromEntries(
      Object.entries(v).map(([k, val]) => [k, deepUnwrapJsonStrings(val)])
    );
  }
  return v;
};

// Simple helper to parse JSON string values
export const parseValue = (value) => {
  if (typeof value !== "string") return value;

  // Don't try to parse if it's clearly not JSON
  const trimmed = value.trim();
  if (
    !trimmed.startsWith("{") &&
    !trimmed.startsWith("[") &&
    !trimmed.startsWith('"') &&
    trimmed !== "true" &&
    trimmed !== "false" &&
    trimmed !== "null" &&
    !/^-?\d+\.?\d*$/.test(trimmed)
  ) {
    return value;
  }

  try {
    return deepUnwrapJsonStrings(JSON.parse(value));
  } catch {
    return value;
  }
};

const categoryPathToCamelCase = (path) => {
  return path
    .split("/")
    .map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join("");
};

// Categories in the V4 settings payload come in two shapes:
//   Legacy:  { "<category>": [field, field, ...] }
//   Emerit:  { "<category>": { subtitle, icon, settings: [field, ...] } }
// This helper normalizes both into a `{ fields, meta }` tuple.
const normalizeCategoryValue = (value) => {
  if (Array.isArray(value)) return { fields: value, meta: null };
  if (value && Array.isArray(value.settings)) {
    const { settings: fields, ...meta } = value;
    return { fields, meta };
  }
  return { fields: null, meta: null };
};

// Parse entire settings object into structured config
export const parseSettings = (settings) => {
  if (!settings) return {};

  const parsedConfig = {};

  try {
    Object.entries(settings).forEach(([category, rawValue]) => {
      if (category === "-") return; // Skip invalid categories

      const { fields, meta } = normalizeCategoryValue(rawValue);
      if (!fields) return; // Skip categories with an unrecognized shape

      const parts = category.split("/");

      // Navigate/create nested structure
      let current = parsedConfig;
      parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;
        const isTopLevel = index === 0;
        let headerField;

        if (!current[part]) {
          // Only add type: "GROUP" for nested (non-top-level) categories
          const headerFieldName = categoryPathToCamelCase(category);
          headerField = fields.find((f) => f.name === headerFieldName);

          current[part] = isTopLevel
            ? {}
            : {
                type: "GROUP",
                name: part,
                ...(headerField ? { headerField } : {}),
                ...(meta || {}),
              };
        }

        if (isLast) {
          let fieldToParse = fields;

          // Last part: add the fields here
          if (headerField) {
            fieldToParse = fields.filter((f) => f.name !== headerField.name);
          }

          fieldToParse.forEach((field) => {
            const fieldConfig = {
              name: field.name,
              schema: field.schema,
              desiredState: parseValue(field.desired_state),
              reportedState: parseValue(field.reported_state),
            };

            // Pass inputHelper if present in schema
            if (field.schema?.inputHelper) {
              fieldConfig.inputHelper = field.schema.inputHelper;
            }

            // Parse hardValue and add disabledValue if applicable
            if (field.schema?.hardValue) {
              const { field: watchedField, values: hardValueMap } =
                field.schema.hardValue;

              // If field is empty string and there's a mapping for it, add disabledValue
              if (watchedField === "" && hardValueMap && "" in hardValueMap) {
                fieldConfig.schema.disabledValue = hardValueMap[""];
              }
            }

            current[part][field.name] = fieldConfig;
          });
        } else {
          // Intermediate part: continue navigating
          current = current[part];
        }
      });
    });

    return parsedConfig;
  } catch (e) {
    console.error("Error parsing settings:", e);
    return null;
  }
};

const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

// Changed = desired is set and differs from reported. An empty desired never
// counts (no false positives on fresh devices) unless it clears a reported list.
const settingValuesDiffer = (desired, reported) => {
  if (Array.isArray(desired) && desired.length === 0) {
    return Array.isArray(reported) && reported.length > 0;
  }
  if (isEmptyValue(desired)) return false;
  return !_isEqual(desired, reported);
};

// A double-encoded array is still a string after parseValue and would never
// equal the other side, so decode it (up to twice) as parseValue would.
const decodeArrayValue = (value) => {
  let decoded = value;
  for (let i = 0; i < 2 && typeof decoded === "string"; i += 1) {
    try {
      decoded = JSON.parse(decoded);
    } catch {
      return value;
    }
  }
  return Array.isArray(decoded) ? deepUnwrapJsonStrings(decoded) : value;
};

// Custom views have their own editor (see Renderer).
export const isCustomViewsSetting = (schema, groupName, name) =>
  schema?.inputHelper === "customViews" ||
  (groupName === "general" && name === "appPages");

// Items saved before a boolean existed lack its key; compare it as off.
const fillMissingItemBooleans = (list, itemSchema) => {
  const properties = itemSchema?.properties;
  if (!Array.isArray(list) || !properties) return list;
  const booleanKeys = Object.keys(properties).filter(
    (key) => properties[key]?.type?.toLowerCase() === "boolean"
  );
  if (booleanKeys.length === 0) return list;
  return list.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;
    const filled = { ...item };
    booleanKeys.forEach((key) => {
      if (filled[key] === undefined) filled[key] = false;
    });
    return filled;
  });
};

// Formik reads field ids as dotted paths, but setting names may contain dots
// and extend each other ("trigger.inactivity", "trigger.inactivity.x").
const NAME_DOT = "·";
export const fieldKey = (name) => String(name).split(".").join(NAME_DOT);

export const settingIdOf = (fieldId) =>
  String(fieldId).split(NAME_DOT).join(".");

const getPath = (values, path) =>
  path.split(".").reduce((obj, key) => {
    // Handle array notation like "field[0]"
    const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayName, index] = arrayMatch;
      return obj?.[arrayName]?.[parseInt(index)];
    }
    return obj?.[key];
  }, values);

// Resolve a sibling field's value (for showOn/hideOn). Shared with the renderer.
export const getCurrentFieldValue = (
  formValues,
  currentFieldId,
  targetFieldName
) => {
  const pathParts = currentFieldId.split(".");
  pathParts.pop(); // Remove current field name → parent path

  const parent =
    pathParts.length > 0 ? getPath(formValues, pathParts.join(".")) : formValues;
  const siblingKey = fieldKey(targetFieldName);
  if (parent && typeof parent === "object" && siblingKey in parent) {
    return parent[siblingKey];
  }

  const targetPath =
    pathParts.length > 0
      ? `${pathParts.join(".")}.${targetFieldName}`
      : targetFieldName;

  return getPath(formValues, targetPath);
};

// Field visibility from its schema's showOn/hideOn conditions. Single source of
// truth for the renderer and the change-counting below.
export const isFieldVisible = (schema, id, values) => {
  if (!schema) return true;

  if (schema.showOn) {
    return Object.entries(schema.showOn).every(([fieldName, expectedValue]) => {
      const currentValue = getCurrentFieldValue(values, id, fieldName);
      if (Array.isArray(expectedValue)) return expectedValue.includes(currentValue);
      return currentValue === expectedValue;
    });
  }

  if (schema.hideOn) {
    return !Object.entries(schema.hideOn).every(([fieldName, expectedValue]) => {
      if (fieldName === "" && expectedValue === "") return true;
      const currentValue = getCurrentFieldValue(values, id, fieldName);
      if (Array.isArray(expectedValue)) return expectedValue.includes(currentValue);
      return currentValue === expectedValue;
    });
  }

  return true;
};

// One entry per visible changed field: `from` = reported (device), `to` =
// desired (to push). `values` gate showOn/hideOn so hidden fields are excluded.
// Ids match the renderers' `<category>.<...>.<fieldName>`; discard reads `schema`.
export const collectSettingsChanges = (settings, values) => {
  const changes = [];
  if (!settings) return changes;

  try {
    Object.entries(settings).forEach(([category, rawValue]) => {
      if (category === "-") return; // Skip invalid categories

      const { fields } = normalizeCategoryValue(rawValue);
      if (!fields) return; // Skip categories with an unrecognized shape

      const basePath = category.split("/").join(".");

      fields.forEach((field) => {
        if (!field?.name) return;

        const id = `${basePath}.${fieldKey(field.name)}`;

        // Skip hidden fields so the count/diff match the on-screen indicators.
        if (!isFieldVisible(field.schema, id, values)) return;

        const isArray = field.schema?.type?.toLowerCase() === "array";
        const decode = isArray ? decodeArrayValue : (v) => v;
        const desired = decode(parseValue(field.desired_state));
        const reported = decode(parseValue(field.reported_state));
        // from/to stay raw; custom views read a missing `enabled` as on.
        const comparable =
          isArray && !isCustomViewsSetting(field.schema, basePath, field.name)
            ? (v) => fillMissingItemBooleans(v, field.schema.items)
            : (v) => v;
        if (settingValuesDiffer(comparable(desired), comparable(reported))) {
          changes.push({
            id,
            name: field.name,
            category: basePath,
            schema: field.schema,
            from: reported,
            to: desired,
          });
        }
      });
    });
  } catch (e) {
    console.error("[DevConV4] collectSettingsChanges failed:", e);
  }

  return changes;
};

// Ids of fields with unsaved changes; drives the indicators and the count.
export const collectChangedSettings = (settings, values) =>
  new Set(collectSettingsChanges(settings, values).map((c) => c.id));

// The save badge's count: one per setting, but custom views count each view
// change, since they are all one setting.
export const countSettingChanges = (changes) =>
  (changes || []).reduce(
    (count, c) =>
      count +
      (isCustomViewsSetting(c.schema, c.category, c.name)
        ? Math.max(1, describeAppPagesChanges(c.from, c.to).length)
        : 1),
    0
  );

// Setting names under these top-level categories.
export const settingNamesInCategories = (settings, categories) => {
  const names = new Set();
  Object.entries(settings || {}).forEach(([category, rawValue]) => {
    if (!categories.includes(category.split("/")[0])) return;
    (normalizeCategoryValue(rawValue).fields || []).forEach((field) => {
      if (field?.name) names.add(field.name);
    });
  });
  return names;
};

export const generateDefaultValues = (settings) => {
  if (!settings) return {};

  const defaults = {};

  try {
    Object.entries(settings).forEach(([category, rawValue]) => {
      const { fields } = normalizeCategoryValue(rawValue);
      if (!fields) return; // unrecognized shape — skip gracefully
      const parts = category.split("/");

      if (parts.length === 1) {
        // Top-level category - flat structure; a subcategory may come first.
        defaults[category] = defaults[category] || {};
        fields.forEach((field) => {
          // Check for hardValue with empty field first
          if (field.schema?.hardValue) {
            const { field: watchedField, values: hardValueMap } =
              field.schema.hardValue;
            if (watchedField === "" && hardValueMap && "" in hardValueMap) {
              defaults[category][fieldKey(field.name)] = hardValueMap[""];
              return;
            }
          }

          const desired = field.desired_state;
          if (desired !== "" && desired !== undefined && desired !== null) {
            defaults[category][fieldKey(field.name)] = parseValue(desired);
          } else {
            defaults[category][fieldKey(field.name)] = generateDefaultFromSchema(
              field.schema,
            );
          }
        });
      } else {
        // Nested category - create nested structure
        let current = defaults;
        parts.forEach((part, index) => {
          if (!current[part]) {
            current[part] = {};
          }

          if (index < parts.length - 1) {
            // Not the last part, continue navigating
            current = current[part];
          } else {
            // Last part: add field values
            fields.forEach((field) => {
              // Check for hardValue with empty field first
              if (field.schema?.hardValue) {
                const { field: watchedField, values: hardValueMap } =
                  field.schema.hardValue;
                if (watchedField === "" && hardValueMap && "" in hardValueMap) {
                  current[part][fieldKey(field.name)] = hardValueMap[""];
                  return;
                }
              }

              const desired = field.desired_state;
              if (desired !== "" && desired !== undefined && desired !== null) {
                current[part][fieldKey(field.name)] = parseValue(desired);
              } else {
                current[part][fieldKey(field.name)] = generateDefaultFromSchema(
                  field.schema,
                );
              }
            });
          }
        });
      }
    });
  } catch (e) {
    console.error("[DevConV4] generateDefaultValues failed:", e);
    return {};
  }

  return defaults;
};

const buildYupFromSchema = (schema) => {
  if (!schema || !schema.type) {
    return Yup.mixed();
  }

  const schemaType = schema.type.toLowerCase();

  switch (schemaType) {
    case "boolean":
      return Yup.boolean().required();

    case "number":
    case "integer": {
      let yupSchema = Yup.number();
      if (schema.minimum !== undefined)
        yupSchema = yupSchema.min(schema.minimum);
      if (schema.maximum !== undefined)
        yupSchema = yupSchema.max(schema.maximum);
      return yupSchema.required();
    }

    case "string": {
      let yupSchema = Yup.string();

      if (schema.inputHelper === "email") {
        yupSchema = yupSchema.email("invalidEmail");
      }

      if (schema.inputHelper === "phoneNumber") {
        yupSchema = yupSchema.matches(/^\+?\d{1,20}$/, "invalidPhoneNumber");
      }

      if (schema.validations) {
        if (schema.validations.minLength !== undefined) {
          yupSchema = yupSchema.min(schema.validations.minLength);
        }
        if (schema.validations.maxLength !== undefined) {
          yupSchema = yupSchema.max(schema.validations.maxLength);
        }
      }
      return yupSchema.required("requiredField");
    }

    case "enum":
      return Yup.string()
        .oneOf(schema.values || [])
        .required();

    case "array": {
      const itemSchema = schema.items
        ? buildYupFromSchema(schema.items)
        : Yup.mixed();
      let yupSchema = Yup.array().of(itemSchema);

      if (schema.validations?.maxLength !== undefined) {
        yupSchema = yupSchema.max(schema.validations.maxLength);
      }
      if (schema.validations?.minLength !== undefined) {
        yupSchema = yupSchema.min(schema.validations.minLength);
      }

      return yupSchema;
    }

    case "object": {
      if (!schema.properties) {
        return Yup.object();
      }

      const shape = {};
      Object.entries(schema.properties).forEach(([key, propertySchema]) => {
        shape[key] = buildYupFromSchema(propertySchema);
      });

      return Yup.object().shape(shape);
    }

    default:
      return Yup.mixed();
  }
};

export const buildValidationSchema = (settings) => {
  if (!settings || !settings.settings) return Yup.object();

  const categories = {};

  try {
    Object.entries(settings.settings).forEach(([category, rawValue]) => {
      const { fields } = normalizeCategoryValue(rawValue);
      if (!fields) return;
      const parts = category.split("/");

      if (parts.length === 1) {
        // Top-level category - flat structure
        const shape = {};
        fields.forEach((field) => {
          shape[field.name] = buildYupFromSchema(field.schema);
        });
        categories[category] = Yup.object().shape(shape);
      } else {
        // Nested category - create nested structure
        let current = categories;
        parts.forEach((part, index) => {
          if (!current[part]) {
            current[part] = {};
          }

          if (index < parts.length - 1) {
            // Not the last part, continue navigating
            current = current[part];
          } else {
            // Last part: add field schemas
            fields.forEach((field) => {
              current[part][field.name] = buildYupFromSchema(field.schema);
            });
          }
        });
      }
    });
  } catch (e) {
    console.error("[DevConV4] buildValidationSchema failed:", e);
    return Yup.object();
  }

  // Recursively convert nested objects to Yup schemas (only for nested categories)
  const buildNestedSchema = (obj) => {
    const shape = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (Yup.isSchema(value)) {
        shape[key] = value;
      } else if (typeof value === "object" && value !== null) {
        shape[key] = Yup.object().shape(buildNestedSchema(value));
      }
    });
    return shape;
  };

  return Yup.object().shape(buildNestedSchema(categories));
};

export function generateDefaultFromSchema(schema) {
  if (!schema?.type) return null;

  const schemaType = schema.type.toLowerCase();

  switch (schemaType) {
    case "boolean":
      return false;
    case "enum":
      return schema.values?.[0] || "";
    case "string":
      return "";
    case "number":
    case "integer":
      return schema.minimum ?? schema.validations?.min ?? 0;
    case "array":
      return [];
    case "object":
      if (!schema.properties) return {};

      const defaultObject = {};
      Object.entries(schema.properties).forEach(([key, propertySchema]) => {
        defaultObject[key] = generateDefaultFromSchema(propertySchema);
      });
      return defaultObject;
    default:
      return null;
  }
}

export const sortFields = (schemaA, schemaB) => {
  const posA = schemaA?.position;
  const posB = schemaB?.position;

  // Both have positions - sort by position value
  if (posA != null && posB != null) {
    return posA - posB;
  }

  // Only A has position - A comes first
  if (posA != null) {
    return -1;
  }

  // Only B has position - B comes first
  if (posB != null) {
    return 1;
  }

  // Neither has position - maintain original order (stable sort)
  return 0;
};

// Natural, locale-aware compare so "tone_2" sorts before "tone_10".
const NAT_COLLATOR =
  typeof Intl !== "undefined" && Intl.Collator
    ? new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
    : null;

export const naturalCompare = (a, b) => {
  const sa = String(a ?? "");
  const sb = String(b ?? "");
  if (NAT_COLLATOR) return NAT_COLLATOR.compare(sa, sb);
  return sa.localeCompare(sb, undefined, { numeric: true });
};

// "no selection" values, pinned to the top of enum lists regardless of casing.
const NONE_LIKE = new Set(["none", "off", "disabled", "", "-1", "null"]);
const isNoneLike = (v) => NONE_LIKE.has(String(v ?? "").trim().toLowerCase());

// Stable enum <Select> order: none-like first, then natural. Copies the array.
export const sortEnumOptions = (values) => {
  if (!Array.isArray(values)) return [];
  return [...values].sort((a, b) => {
    const na = isNoneLike(a);
    const nb = isNoneLike(b);
    if (na !== nb) return na ? -1 : 1;
    return naturalCompare(a, b);
  });
};

// Human-readable label for a `duration_in_seconds` enum value ("600" -> "10 min",
// "86400" -> "24 h"). Non-numeric sentinels ("disabled", "moreThan24Hours",
// "none") get their own translated label.
export const formatDurationLabel = (value, t) => {
  const tr = (key, fallback) =>
    t ? t(key, { defaultValue: fallback }) : fallback;
  const s = String(value);

  if (s === "disabled") return tr("disabled", "Disabled");
  if (s === "moreThan24Hours") return tr("moreThan24Hours", "> 24 h");
  if (s === "none") return tr("none", "None");

  const n = Number(s);
  if (!Number.isFinite(n)) return tr(s, s);
  if (n === 0) return "0";
  if (n < 60) return `${n} ${tr("secondsShort", "sec")}`;
  if (n < 3600 && n % 60 === 0) return `${n / 60} ${tr("minutesShort", "min")}`;
  if (n < 86400 && n % 3600 === 0)
    return `${n / 3600} ${tr("hoursShort", "h")}`;
  if (n % 86400 === 0) return `${n / 86400} ${tr("daysShort", "d")}`;
  // Non-round values: round to the nearest minute.
  return `${Math.round(n / 60)} ${tr("minutesShort", "min")}`;
};

// Readable fallback label for an untranslated key ("tone_12" -> "Tone 12").
export const humanizeSettingKey = (key) => {
  if (key == null) return "";
  const spaced = String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

// Render a setting value for display: bool -> On/Off, enum -> label,
// empty -> em dash, array/object -> compact summary. `t` is optional.
export const formatSettingValue = (value, schema, t) => {
  const translate = (key) =>
    t
      ? t(String(key), { defaultValue: humanizeSettingKey(key) })
      : humanizeSettingKey(key);

  if (isEmptyValue(value)) {
    return t ? t("notSet", { defaultValue: "—" }) : "—";
  }

  if (typeof value === "boolean") {
    return value
      ? t
        ? t("on", { defaultValue: "On" })
        : "On"
      : t
      ? t("off", { defaultValue: "Off" })
      : "Off";
  }

  const type = schema?.type?.toLowerCase();
  if (type === "enum") return translate(value);

  if (Array.isArray(value)) {
    const count = value.length;
    const items = t ? t("items", { defaultValue: "items" }) : "items";
    return `${count} ${items}`;
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

// Emerit devices fetch their configuration themselves, so whether one has taken
// it is a question only the platform can answer:
// GET /v1/devices/{id}/vendor-specific/config-state -> { flags: [...] }.
export const EMERIT_CONFIG_NOT_APPLIED = "emerit-config-not-applied";
export const EMERIT_CONFIG_MISSING = "emerit-config-missing";
export const EMERIT_FIRMWARE_UPDATE_AVAILABLE =
  "emerit-firmware-update-available";

// False for an unread state, so nothing is ever claimed on a failed read.
export const hasDeviceFlag = (configState, flag) =>
  Array.isArray(configState?.flags) && configState.flags.includes(flag);
